"use client";

import React from "react";
import { cn } from "utils";
import {
  PORTFOLIO_GHOST_EMPTY_ASSETS,
  PORTFOLIO_GHOST_IMAGE_DIMENSIONS,
} from "./constants";
import { GhostActionCards } from "./GhostActionCards";
import { GhostPreviewImage } from "./GhostPreviewImage";
import { LinkAccountBar, type LinkAccountBarVariant } from "./LinkAccountBar";
import { PORTFOLIO_GHOST_PC_CONTENT_MAX_WIDTH_PX } from "./linkAccountBarTokens";
import { useIsPc } from "@/src/hooks";

export interface PortfolioGhostStateProps {
  variant: LinkAccountBarVariant;
  onImportClick?: () => void;
  onUploadClick?: () => void;
  onDashboardClick?: () => void;
  className?: string;
}

/**
 * Unlinked portfolio ghost preview: static images + non-interactive action cards + link CTA.
 * PC: flex justify-between fills available height (Figma `94741:252458`).
 */
export const PortfolioGhostState: React.FC<PortfolioGhostStateProps> = ({
  variant,
  onImportClick,
  onUploadClick,
  onDashboardClick,
  className,
}) => {
  const [isPc] = useIsPc();

  return (
    <div
      className={cn(
        "flex w-full min-h-0 flex-1 flex-col",
        isPc && "mx-auto max-w-[768px] justify-between pb-6 relative",
        className,
      )}
      style={
        isPc
          ? { maxWidth: `${PORTFOLIO_GHOST_PC_CONTENT_MAX_WIDTH_PX}px` }
          : undefined
      }
      data-testid="portfolio-ghost-state"
    >
      <div
        className={cn(
          "flex w-full flex-col",
          isPc ? "gap-6" : "gap-3 px-4 pb-4",
        )}
      >
        {isPc ? (
          <>
            <GhostPreviewImage
              src={PORTFOLIO_GHOST_EMPTY_ASSETS.chipsPc}
              width={PORTFOLIO_GHOST_IMAGE_DIMENSIONS.chipsPc.width}
              height={PORTFOLIO_GHOST_IMAGE_DIMENSIONS.chipsPc.height}
              testId="chips-pc"
            />
            <GhostActionCards />
            <GhostPreviewImage
              src={PORTFOLIO_GHOST_EMPTY_ASSETS.statsPc}
              width={PORTFOLIO_GHOST_IMAGE_DIMENSIONS.statsPc.width}
              height={PORTFOLIO_GHOST_IMAGE_DIMENSIONS.statsPc.height}
              testId="stats-pc"
            />
            <GhostPreviewImage
              src={PORTFOLIO_GHOST_EMPTY_ASSETS.chartsPc}
              width={PORTFOLIO_GHOST_IMAGE_DIMENSIONS.chartsPc.width}
              height={PORTFOLIO_GHOST_IMAGE_DIMENSIONS.chartsPc.height}
              testId="charts-pc"
            />
          </>
        ) : (
          <>
            <GhostPreviewImage
              src={PORTFOLIO_GHOST_EMPTY_ASSETS.headerH5}
              width={PORTFOLIO_GHOST_IMAGE_DIMENSIONS.headerH5.width}
              height={PORTFOLIO_GHOST_IMAGE_DIMENSIONS.headerH5.height}
              testId="header-h5"
            />
            <GhostActionCards />
            <GhostPreviewImage
              src={PORTFOLIO_GHOST_EMPTY_ASSETS.historyH5}
              width={PORTFOLIO_GHOST_IMAGE_DIMENSIONS.historyH5.width}
              height={PORTFOLIO_GHOST_IMAGE_DIMENSIONS.historyH5.height}
              testId="history-h5"
            />
            <GhostPreviewImage
              src={PORTFOLIO_GHOST_EMPTY_ASSETS.assetH5}
              width={PORTFOLIO_GHOST_IMAGE_DIMENSIONS.assetH5.width}
              height={PORTFOLIO_GHOST_IMAGE_DIMENSIONS.assetH5.height}
              testId="asset-h5"
            />
          </>
        )}
      </div>

      <LinkAccountBar
        variant={variant}
        layout={isPc ? "pc-bottom" : "h5-pinned"}
        onImportClick={onImportClick}
        onUploadClick={onUploadClick}
        onDashboardClick={onDashboardClick}
      />
    </div>
  );
};
